/**
 * PaymentSection Component
 * Displays payment status, generates payment links, and manages payment workflow
 * Integrates with Cashfree payment gateway
 *
 * @author SAUBHAGYA Development Team
 * @since US-PAY-001
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  CreditCard,
  Send,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertTriangle,
  Copy,
  Mail,
  IndianRupee,
  Truck,
  Zap,
  TestTube,
} from 'lucide-react';
import { toast } from 'sonner';
import type { OrderDetails, PaymentStatus, PaymentLinkResponse, PaymentStatusResponse } from '@/types/order';
import type { CheckPaymentSyncResponse } from '@/services/commerce/orderService';

interface PaymentSectionProps {
  order: OrderDetails;
  onGeneratePaymentLink: () => Promise<PaymentLinkResponse>;
  onRefreshPaymentStatus: () => Promise<PaymentStatusResponse>;
  onCheckAndSyncPaymentStatus: () => Promise<CheckPaymentSyncResponse>;
  onMarkPaymentAsPaid: () => Promise<CheckPaymentSyncResponse>;
  onOpenAssignTransportModal: () => void;
  paymentLoading: boolean;
}

/**
 * Get badge variant based on payment status
 */
function getPaymentStatusVariant(status: PaymentStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'PAID':
    case 'COMPLETED':
      return 'default';
    case 'PENDING':
      return 'secondary';
    case 'FAILED':
      return 'destructive';
    case 'PARTIAL':
      return 'outline';
    default:
      return 'secondary';
  }
}

/**
 * Get payment status display text
 */
function getPaymentStatusText(status: PaymentStatus): string {
  switch (status) {
    case 'PAID':
    case 'COMPLETED':
      return 'Payment Completed';
    case 'PENDING':
      return 'Payment Pending';
    case 'FAILED':
      return 'Payment Failed';
    case 'PARTIAL':
      return 'Partial Payment';
    case 'REFUNDED':
      return 'Refunded';
    default:
      return status;
  }
}

/**
 * Get payment status icon
 */
function getPaymentStatusIcon(status: PaymentStatus) {
  switch (status) {
    case 'PAID':
    case 'COMPLETED':
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'PENDING':
      return <Clock className="h-4 w-4 text-amber-600" />;
    case 'FAILED':
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    default:
      return <CreditCard className="h-4 w-4" />;
  }
}

/**
 * Format currency in Indian Rupees
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(amount);
}

export function PaymentSection({
  order,
  onGeneratePaymentLink,
  onRefreshPaymentStatus,
  onCheckAndSyncPaymentStatus,
  onMarkPaymentAsPaid,
  onOpenAssignTransportModal,
  paymentLoading,
}: PaymentSectionProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  const paymentStatus = order.payment?.paymentStatus || 'PENDING';
  const paymentLink = order.paymentLink || order.payment?.paymentLink;
  const isPaymentComplete = paymentStatus === 'PAID' || paymentStatus === 'COMPLETED';
  const isPaymentPending = paymentStatus === 'PENDING';
  const hasPaymentLink = !!paymentLink;
  const hasTransport = !!(order.transport?.transportRequestId);

  // Show payment section for CONFIRMED orders and later stages
  const showPaymentSection = order.orderStatus === 'CONFIRMED' ||
                             order.orderStatus === 'READY' ||
                             order.orderStatus === 'IN_TRANSIT' ||
                             order.orderStatus === 'DELIVERED' ||
                             order.orderStatus === 'COMPLETED';

  /**
   * Handle generate payment link
   */
  async function handleGeneratePaymentLink() {
    setIsGenerating(true);
    try {
      const response = await onGeneratePaymentLink();
      toast.success('Payment link generated and sent to buyer', {
        description: `Payment link has been emailed to the customer.`,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate payment link');
    } finally {
      setIsGenerating(false);
    }
  }

  /**
   * Handle refresh payment status
   */
  async function handleRefreshStatus() {
    try {
      const response = await onRefreshPaymentStatus();

      if (response.paymentStatus === 'PAID' || response.paymentStatus === 'COMPLETED') {
        toast.success('Payment completed!', {
          description: 'You can now assign transport for delivery.',
        });
        setIsPolling(false);
      } else {
        toast.info('Payment status updated', {
          description: `Current status: ${getPaymentStatusText(response.paymentStatus)}`,
        });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to refresh payment status');
    }
  }

  /**
   * Copy payment link to clipboard
   */
  async function handleCopyLink() {
    if (!paymentLink) return;

    try {
      await navigator.clipboard.writeText(paymentLink);
      toast.success('Payment link copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  }

  /**
   * Open payment link in new tab
   */
  function handleOpenPaymentLink() {
    if (!paymentLink) return;
    window.open(paymentLink, '_blank', 'noopener,noreferrer');
  }

  /**
   * Start polling for payment status
   */
  function startPolling() {
    setIsPolling(true);
    setPollCount(0);
  }

  /**
   * Stop polling
   */
  function stopPolling() {
    setIsPolling(false);
    setPollCount(0);
  }

  /**
   * Handle check and sync payment status from Cashfree
   */
  async function handleCheckAndSyncPaymentStatus() {
    setIsSyncing(true);
    try {
      const response = await onCheckAndSyncPaymentStatus();

      // Case 1: Payment was just confirmed and order updated
      if (response.paymentUpdated === true) {
        toast.success('Payment confirmed!', {
          description: response.transportRequestCreated
            ? 'Transport request created successfully.'
            : 'You can now create a transport request.',
        });
      }
      // Case 2: API error
      else if (response.cashfreeLinkStatus === 'API_ERROR') {
        toast.error('Failed to check Cashfree', {
          description: response.error || 'Could not reach Cashfree API',
        });
      }
      // Case 3: Already paid in our system
      else if (response.currentPaymentStatus === 'PAID') {
        toast.info('Payment already processed');
      }
      // Case 4: Payment still pending
      else {
        toast.info('Payment not yet completed', {
          description: response.hint || `Cashfree status: ${response.cashfreeLinkStatus || 'Unknown'}`,
        });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to sync payment status');
    } finally {
      setIsSyncing(false);
    }
  }

  /**
   * Handle mark payment as paid (FOR TESTING ONLY)
   */
  async function handleMarkPaymentAsPaid() {
    setIsMarkingPaid(true);
    try {
      const response = await onMarkPaymentAsPaid();

      toast.success('Payment marked as PAID (Testing)', {
        description: response.transportRequestCreated
          ? 'Transport request created successfully.'
          : 'Order updated successfully.',
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to mark payment as paid');
    } finally {
      setIsMarkingPaid(false);
    }
  }

  // Auto-poll for payment status when polling is enabled
  useEffect(() => {
    if (!isPolling || isPaymentComplete) return;

    const pollInterval = setInterval(async () => {
      setPollCount((prev) => prev + 1);

      try {
        const response = await onRefreshPaymentStatus();

        if (response.paymentStatus === 'PAID' || response.paymentStatus === 'COMPLETED') {
          toast.success('Payment completed!', {
            description: 'You can now assign transport for delivery.',
          });
          setIsPolling(false);
        }
      } catch (error) {
        console.error('Poll error:', error);
      }

      // Stop polling after 10 attempts (30 seconds)
      if (pollCount >= 10) {
        setIsPolling(false);
        toast.info('Polling stopped', {
          description: 'Click "Check Status" to manually refresh.',
        });
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [isPolling, isPaymentComplete, pollCount, onRefreshPaymentStatus]);

  if (!showPaymentSection) {
    return null;
  }

  return (
    <Card className="border-2 border-blue-100 bg-gradient-to-br from-white to-blue-50/30 shadow-lg">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <CreditCard className="h-5 w-5 text-blue-600" />
          Payment Status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Payment Status Badge */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Status</span>
          <Badge
            variant={getPaymentStatusVariant(paymentStatus)}
            className="flex items-center gap-1.5"
          >
            {getPaymentStatusIcon(paymentStatus)}
            {getPaymentStatusText(paymentStatus)}
          </Badge>
        </div>

        <Separator />

        {/* Amount Details */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total Amount</span>
            <span className="font-semibold flex items-center gap-1">
              <IndianRupee className="h-3.5 w-3.5" />
              {formatCurrency(order.payment?.totalAmount || order.gstBreakdown?.totalAmount || 0).replace('₹', '')}
            </span>
          </div>

          {order.payment?.paidAmount > 0 && (
            <div className="flex items-center justify-between text-green-600">
              <span className="text-sm">Amount Paid</span>
              <span className="font-medium flex items-center gap-1">
                <IndianRupee className="h-3.5 w-3.5" />
                {formatCurrency(order.payment.paidAmount).replace('₹', '')}
              </span>
            </div>
          )}

          {order.payment?.balanceDue > 0 && !isPaymentComplete && (
            <div className="flex items-center justify-between text-amber-600">
              <span className="text-sm">Balance Due</span>
              <span className="font-medium flex items-center gap-1">
                <IndianRupee className="h-3.5 w-3.5" />
                {formatCurrency(order.payment.balanceDue).replace('₹', '')}
              </span>
            </div>
          )}
        </div>

        <Separator />

        {/* Payment Completed Message */}
        {isPaymentComplete && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              Payment has been successfully completed. You can now proceed to assign transport for delivery.
              {order.paymentCompletedAt && (
                <span className="block text-xs mt-1 text-green-600">
                  Completed on: {new Date(order.paymentCompletedAt).toLocaleString()}
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Payment Link Section */}
        {!isPaymentComplete && (
          <div className="space-y-3">
            {/* Generate Payment Link Button */}
            {!hasPaymentLink && isPaymentPending && (
              <Button
                onClick={handleGeneratePaymentLink}
                disabled={isGenerating || paymentLoading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {isGenerating || paymentLoading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Payment Link to Buyer
                  </>
                )}
              </Button>
            )}

            {/* Payment Link Display */}
            {hasPaymentLink && (
              <div className="space-y-3">
                <Alert className="border-amber-200 bg-amber-50">
                  <Mail className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    Payment link has been sent to the buyer's email. Waiting for payment confirmation.
                  </AlertDescription>
                </Alert>

                <div className="flex flex-col gap-2">
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Payment Link:</p>
                    <p className="text-sm font-mono break-all text-blue-600">{paymentLink}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyLink}
                      className="flex-1"
                    >
                      <Copy className="mr-2 h-3.5 w-3.5" />
                      Copy Link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleOpenPaymentLink}
                      className="flex-1"
                    >
                      <ExternalLink className="mr-2 h-3.5 w-3.5" />
                      Open Link
                    </Button>
                  </div>
                </div>

                {/* Check Status / Auto-Poll Section */}
                <div className="flex gap-2">
                  <Button
                    variant={isPolling ? 'destructive' : 'secondary'}
                    size="sm"
                    onClick={isPolling ? stopPolling : startPolling}
                    className="flex-1"
                  >
                    {isPolling ? (
                      <>
                        <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                        Stop Auto-Check ({10 - pollCount})
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-3.5 w-3.5" />
                        Auto-Check Status
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshStatus}
                    disabled={paymentLoading}
                    className="flex-1"
                  >
                    {paymentLoading ? (
                      <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-3.5 w-3.5" />
                    )}
                    Check Status
                  </Button>
                </div>

                <Separator />

                {/* Sync from Cashfree (for local testing) */}
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Local Testing: Webhooks can't reach localhost. Use these buttons to manually sync payment status.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCheckAndSyncPaymentStatus}
                      disabled={isSyncing || paymentLoading}
                      className="flex-1"
                    >
                      {isSyncing ? (
                        <>
                          <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <Zap className="mr-2 h-3.5 w-3.5" />
                          Sync from Cashfree
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleMarkPaymentAsPaid}
                      disabled={isMarkingPaid || paymentLoading}
                      className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                    >
                      {isMarkingPaid ? (
                        <>
                          <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                          Marking...
                        </>
                      ) : (
                        <>
                          <TestTube className="mr-2 h-3.5 w-3.5" />
                          Mark Paid (Test)
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Transport Request Section - Show when payment is complete */}
        {isPaymentComplete && !hasTransport && (
          <div className="space-y-3 pt-2">
            <Separator />
            <Alert className="border-blue-200 bg-blue-50">
              <Truck className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Payment is complete! Create a transport request to schedule delivery.
              </AlertDescription>
            </Alert>
            <Button
              onClick={onOpenAssignTransportModal}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <Truck className="mr-2 h-4 w-4" />
              Assign Transport
            </Button>
          </div>
        )}

        {/* Transport Already Assigned */}
        {isPaymentComplete && hasTransport && (
          <div className="pt-2">
            <Separator className="mb-3" />
            <Alert className="border-green-200 bg-green-50">
              <Truck className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Transport request created: <span className="font-semibold">{order.transport?.transportRequestNumber || `#${order.transport?.transportRequestId}`}</span>
                <br />
                <span className="text-xs">Status: {order.transport?.transportStatus || 'Pending'}</span>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Cashfree Order ID */}
        {order.cashfreeOrderId && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              Cashfree Order ID: <span className="font-mono">{order.cashfreeOrderId}</span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
